import axios from "axios";
import Vue from "vue";
import { get, set } from "idb-keyval";
import APICache from "../lib/api-cache";
import { customOfflineMessage } from "../lib/offline";
import { localizacaoAtual } from "../lib/localizacaoAtual";
import InstructActivateGeolocation from "@/templates/Components/InstructActivateGeolocation";

import store from "../store";

const defaultOptions = {
  baseURL: "https://api.sistemaclin.com.br/",
  withCredentials: true,
};

const api = axios.create(defaultOptions);

api.interceptors.request.use(
  async function (config) {
    config.headers["requester"] = "user";
    config.headers["devicecode"] = localStorage.getItem("device_code") ?? null;

    const { method, url } = config;

    const user = await get("user");
    if (user) config.headers["userid"] = user.id;

    config.headers["last-offline-update"] = await get("last-offline-update");

    if (config.force) return config;

    if (method === "get" && config.params === undefined) {
      if ((await APICache.getType(url)) === "static") {
        if (await APICache.isCacheable(url)) {
          if (await APICache.isCached(url)) {
            let data;
            try {
              data = await APICache.fromCache(url);
            } catch (reason) {
              console.warn(
                method,
                url,
                "não foi possível usar resposta em cache:",
                reason,
              );
              data = undefined;
            }
            if (data !== undefined) {
              console.warn(method, url, status, "usando resposta em cache");
              throw { cached: true, data, config };
            }
          }
        }
      }
    }

    if (method === "post" || method === "put") {
      const retornoLocalizacaoAtual = await localizacaoAtual();

      if (retornoLocalizacaoAtual.error) {
        config.headers["localizacao-atual"] = false;
      } else {
        config.headers["localizacao-atual"] = true;
      }

      let { latitude, longitude } = retornoLocalizacaoAtual;

      config.headers["latitude"] = latitude;
      config.headers["longitude"] = longitude;
    }

    return config;
  },
  async function (error) {
    // console.log(error);
    return error;
  },
);

api.interceptors.response.use(
  async function (response) {
    const { method, url } = response.config;

    store.commit("online");

    const sync = response.data.sync;
    if (sync) delete response.data.sync;

    if (method === "get" && response.config.params === undefined) {
      APICache.isCacheable(url)
        .then((cacheable) => {
          if (cacheable)
            APICache.cache(url, null, response.data).catch((reason) => {
              console.log(
                method,
                url,
                "não foi possível armazenar resposta em cache:",
                reason,
              );
            });
        })
        .catch((reason) => {
          console.log(
            method,
            url,
            "não foi possível verificar se a resposta é armazenável em cache:",
            reason,
          );
        });
    }

    if (sync)
      store.dispatch("sync").catch((reason) => {
        console.log(
          "Não foi possível executar a sincronização das rotas:",
          reason,
        );
      });

    const urlsWithGeolocation = [
      "/rest-home/evolution", // Evolucao
      "/rest-home/vital-sign/create", // Sinais vitais
      "/rest-home/group-prescription-medicine", // Prescricao Medica
      "/rest-home/care/shift-change/create", // Anotacao de Cuidados
      "/rest-home/duty-control/start-duty", // Começo de Plantão
      "/rest-home/duty-control/end-duty", // Final de Plantão
    ];

    if ((method === "post" || method === "put") && urlsWithGeolocation.includes(response.config.url)) {
      if (response.data.errorGeolocation) {
        const ComponentClass = Vue.extend(InstructActivateGeolocation);

        const instance = new ComponentClass();

        instance.$mount();

        document.body.appendChild(instance.$el);
      }
    }

    return response;
  },
  async function (error) {
    const config = error.config;
    const { method, url, params } = config;

    if (error.response) {
      // A requisição foi feita e o servidor respondeu
      // com o código de status fora do intervalo 2xx
      store.commit("online");

      const status = error.response.status;

      if (status >= 100 && status < 200) {
        console.warn(method, url, status);
      } else if (status >= 300 && status < 400) {
        console.warn(method, url, status);
      } else if (status >= 400 && status < 500) {
        console.warn(method, url, status);

        if (status === 401 /** unauthorized (unauthenticated) */) {
          const { user, permissions } = error.response.data;
          if (user && permissions) {
            await set("user", user);
            await set("permissions", permissions);

            sessionStorage.setItem("user", JSON.stringify(user));
            sessionStorage.setItem("permissions", JSON.stringify(permissions));
            sessionStorage.setItem("rest_home_id", user.rest_home_id);

            return api.request(error.response.config);
          } else {
            console.error(method, url, status, "é necessário fazer login");
            return {
              data: { error: true, errors: [customOfflineMessage(status)] },
            };
          }
        } else if (status === 403 /** forbidden */) {
          console.error(
            method,
            url,
            status,
            "você não tem permissão para acessar este recurso",
          );

          return error.response;
        } else {
          console.error(method, url, status, "código desconhecido");
          return {
            data: { error: true, errors: [customOfflineMessage(status)] },
          };
        }
      } else if (status >= 500 && status < 600) {
        console.warn(method, url, status, "erro de servidor");

        if (method === "get" && params === undefined) {
          if (await APICache.isCacheable(url)) {
            if (await APICache.isCached(url)) {
              let data;
              try {
                data = await APICache.fromCache(url);
              } catch (reason) {
                console.error(
                  method,
                  url,
                  "não foi possível usar resposta em cache:",
                  reason,
                );
                data = undefined;
              }
              if (data !== undefined) {
                console.warn(method, url, status, "usando resposta em cache");
                return { data, config };
              }
            }
          }
        }
      }
    } else if (error.request) {
      console.error(method, url, "servidor não respondeu a requisição");
      // A requisição foi feita mas nenhuma resposta foi recebida
      // `error.request` é a instância do XMLHttpRequest que fez a requisição
      store.commit("offline");
      // console.log(error.request);

      if (method === "get" && params === undefined) {
        if (await APICache.isCacheable(url)) {
          if (await APICache.isCached(url)) {
            let data;
            try {
              data = await APICache.fromCache(url);
            } catch (reason) {
              console.error(
                method,
                url,
                "não foi possível usar resposta em cache:",
                reason,
              );
              data = undefined;
            }
            if (data !== undefined) {
              console.warn(method, url, "usando resposta em cache");
              return { data: { ...data, offline: true }, config };
            }
          }
        }
      }
    } else {
      console.log(method, url, "erro:", error);
      // Algo ocorreu enquanto definia a requisição e disparou um Error
      const { cached, data, method, url } = error;
      if (cached) {
        console.warn(method, url, "usando resposta em cache");
        return { data };
      }

      store.commit("offline");

      /** verificar esse params */
      if (method === "get" && params === undefined) {
        if (await APICache.isCacheable(url)) {
          if (await APICache.isCached(url)) {
            let data;
            try {
              data = await APICache.fromCache(url);
            } catch (reason) {
              console.error(
                method,
                url,
                "não foi possível usar resposta em cache:",
                reason,
              );
              data = undefined;
            }
            if (data !== undefined) {
              return {
                data: { ...data, offline: true },
                config: { method, url },
              };
            }
          }
        }
      }
    }

    console.error(method, url, "usando resposta manual");
    store.commit("offline");
    return {
      data: { error: true, errors: [customOfflineMessage(url)], offline: true },
    };
    // return error;
  },
);

export default api;
